import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldTextarea, FieldToggle, FieldCustom,
} from './detail/DetailShell';
import { StatusBadge } from './ui';
import { usePageActions } from '../contexts/PermissionContext';
import {
  FREQUENCY, FREQUENCY_OPTIONS, WEEKDAYS, RUN_STATUS_META, TRIGGER_LABEL,
  describeSchedule, parseWeekdays,
} from './scheduledTaskShared';

// ZAMANLANMIŞ GÖREV — DETAY (menü 9608).
//
// Ekran GENELDİR: ortadaki "Kriterler" kartı, seçilen görev tipinin ŞEMASINDAN üretilir
// (/api/scheduled-tasks/TaskTypes). Yeni bir görev tipi backend'e eklendiğinde bu dosyaya
// DOKUNULMAZ — combobox'a düşer, açıklaması info kutusunda belirir, kriterleri kendiliğinden çizilir.
// Bu yüzden burada hiçbir tipin adı geçmez.
const API = '/api/scheduled-tasks';

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'data', 'Data') ?? payload; }

/** ISO/DateTimeOffset → <input type="date"> değeri. */
function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR');
}

const todayInput = () => toDateInput(new Date());

const EMPTY_FORM = {
  name: '',
  taskType: '',
  shortNote: '',
  frequency: FREQUENCY.DAILY,
  intervalN: '6',
  runTime: '03:00',
  weekdays: [],
  monthDay: '1',
  startDate: '',
  endDate: '',
  isActive: true,
  params: {},
};

export function ScheduledTaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Zamanlanmış Görev' : 'Zamanlanmış Görev', 'Görev tipi, kriterler ve çalışma takvimi.');

  const [form, setForm] = useState({ ...EMPTY_FORM, startDate: todayInput() });
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);
  const [activeTab, setActiveTab] = useState('definition');
  const [runs, setRuns] = useState([]);
  const [nextRunAt, setNextRunAt] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 4000); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setParam = (k, v) => setForm((c) => ({ ...c, params: { ...c.params, [k]: v } }));

  // ── Görev tipi kataloğu ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/TaskTypes`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => setTaskTypes(unwrap(body) ?? []))
      .catch(() => showToast('error', 'Görev tipleri yüklenemedi'));
  }, []);

  const selectedType = useMemo(
    () => taskTypes.find((t) => t.key === form.taskType) ?? null,
    [taskTypes, form.taskType],
  );

  /** Tip değişince o tipin VARSAYILAN parametrelerini forma bas — kullanıcı boş formla karşılaşmasın. */
  const applyTypeDefaults = useCallback((typeKey) => {
    const type = taskTypes.find((t) => t.key === typeKey);
    const defaults = {};
    (type?.paramSchema ?? []).forEach((f) => {
      if (f.default !== undefined && f.default !== null) defaults[f.key] = f.default;
    });
    setForm((c) => ({ ...c, taskType: typeKey, params: defaults }));
  }, [taskTypes]);

  // ── Kayıt yükleme ──────────────────────────────────────────────────────────────────────────
  const recordToForm = (r) => ({
    name: pick(r, 'name', 'Name') ?? '',
    taskType: pick(r, 'taskType', 'TaskType') ?? '',
    shortNote: pick(r, 'shortNote', 'ShortNote') ?? '',
    frequency: pick(r, 'frequency', 'Frequency') ?? FREQUENCY.DAILY,
    intervalN: String(pick(r, 'intervalN', 'IntervalN') ?? ''),
    runTime: pick(r, 'runTime', 'RunTime') ?? '',
    weekdays: parseWeekdays(r),
    monthDay: String(pick(r, 'monthDay', 'MonthDay') ?? ''),
    startDate: toDateInput(pick(r, 'startDate', 'StartDate')),
    endDate: toDateInput(pick(r, 'endDate', 'EndDate')),
    isActive: !!pick(r, 'isActive', 'IsActive'),
    params: pick(r, 'params', 'Params') ?? {},
  });

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Görev bulunamadı');
      const r = unwrap(await res.json());
      if (!r) throw new Error('Görev bulunamadı');

      setForm(recordToForm(r));
      setNextRunAt(pick(r, 'nextRunAt', 'NextRunAt') ?? null);
      setAudit({
        createdBy: pick(r, 'createdBy', 'CreatedBy'),
        createdDate: pick(r, 'createdDate', 'CreatedDate'),
        updatedBy: pick(r, 'updatedBy', 'UpdatedBy'),
        updatedDate: pick(r, 'updatedDate', 'UpdatedDate'),
      });
    } catch (err) {
      showToast('error', err.message || 'Görev alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const loadRuns = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/Runs?id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (res.ok) setRuns(unwrap(await res.json()) ?? []);
    } catch { /* geçmiş yüklenemedi — sayfanın kalanı çalışmaya devam eder */ }
  }, [id, isNew]);

  useEffect(() => { if (activeTab === 'runs') loadRuns(); }, [activeTab, loadRuns]);

  // ── Kaydetme ───────────────────────────────────────────────────────────────────────────────

  /**
   * Form parametrelerini şemaya göre GERİ TİPLE.
   *
   * HTML <select> her zaman STRING verir; oysa şemadaki bazı seçenek değerleri SAYIDIR
   * (ör. routeDay=7, targetStatus=3). Ham string gönderilseydi backend "7" ile 7'yi ayırt
   * edemez ve parametre sessizce varsayılana düşerdi. Bu yüzden değeri, şemadaki ORİJİNAL
   * seçenek değeriyle eşleştirip onun tipini geri veriyoruz.
   */
  const buildParams = () => {
    const schema = selectedType?.paramSchema ?? [];
    const out = {};

    schema.forEach((f) => {
      const raw = form.params[f.key];

      // Bir toggle'a bağlı alan, toggle kapalıysa hiç gönderilmez.
      if (f.visibleWhen && !form.params[f.visibleWhen]) return;

      if (f.type === 'number') {
        const n = Number.parseInt(raw, 10);
        if (!Number.isNaN(n)) out[f.key] = n;
      } else if (f.type === 'toggle') {
        out[f.key] = !!raw;
      } else if (f.type === 'multiselect') {
        out[f.key] = Array.isArray(raw) ? raw : [];
      } else if (f.type === 'select') {
        const match = (f.options ?? []).find((o) => String(o.value) === String(raw));
        out[f.key] = match ? match.value : raw;
      } else {
        out[f.key] = raw ?? '';
      }
    });

    return out;
  };

  const buildBody = () => ({
    ...(isNew ? {} : { id }),
    name: String(form.name ?? '').trim(),
    taskType: form.taskType,
    shortNote: String(form.shortNote ?? '').trim() || null,
    params: buildParams(),
    frequency: form.frequency,
    intervalN: form.frequency === FREQUENCY.HOURLY ? Number.parseInt(form.intervalN, 10) || null : null,
    runTime: form.runTime || null,
    weekdays: form.frequency === FREQUENCY.WEEKLY ? form.weekdays : [],
    monthDay: form.frequency === FREQUENCY.MONTHLY ? Number.parseInt(form.monthDay, 10) || null : null,
    startDate: form.startDate || todayInput(),
    endDate: form.endDate || null,
    isActive: !!form.isActive,
  });

  const validate = () => {
    if (!String(form.name ?? '').trim()) return 'Görev adı boş olamaz';
    if (!form.taskType) return 'Görev tipi seçilmelidir';
    if (form.frequency === FREQUENCY.WEEKLY && form.weekdays.length === 0) return 'Haftalık görev için en az bir gün seçin';
    if (form.frequency === FREQUENCY.HOURLY && !(Number.parseInt(form.intervalN, 10) > 0)) return 'Saat aralığı 0’dan büyük olmalıdır';
    return null;
  };

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    const error = validate();
    if (error) { showToast('error', error); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'Görev kaydedilemedi');

      if (isNew && andNew) {
        showToast('success', 'Görev eklendi. Yeni göreve devam edebilirsiniz.');
        setForm({ ...EMPTY_FORM, startDate: todayInput() });
        setTried(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      showToast('success', isNew ? 'Görev başarıyla eklendi' : 'Görev başarıyla güncellendi');
      setTimeout(() => navigate('/scheduled-tasks'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'Görev silinemedi');
      showToast('success', 'Görev silindi');
      setTimeout(() => navigate('/scheduled-tasks'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  /** Kaydedilmiş görevi elle koştur. Zamanlama imlecine dokunmaz. */
  const handleRunNow = async () => {
    setRunning(true);
    showToast('info', 'Görev çalıştırılıyor…');
    try {
      const res = await fetch(`${API}/RunNow`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'Görev çalıştırılamadı');

      const result = unwrap(body) ?? {};
      // Görev içindeki hata HTTP 200 ile döner (run logu FAILED'dir) — status'e bakmak ŞART.
      if (result.status === 'FAILED') showToast('error', `Görev hata verdi: ${result.message || 'bilinmeyen hata'}`);
      else showToast('success', result.message || 'Görev tamamlandı');

      setActiveTab('runs');
      loadRuns();
      load();
    } catch (err) {
      showToast('error', err.message || 'Görev çalıştırılamadı');
    } finally {
      setRunning(false);
    }
  };

  // ── Dinamik kriter formu ───────────────────────────────────────────────────────────────────

  const renderParamField = (f) => {
    // Bir toggle'a bağlı alan (ör. "Hedef gün" ← "Belli bir ruta taşı") toggle kapalıyken çizilmez.
    if (f.visibleWhen && !form.params[f.visibleWhen]) return null;

    // `key` bilerek spread'in DIŞINDA veriliyor — React 18, key'i props nesnesiyle birlikte
    // yaymayı uyarı olarak işaretler.
    const common = { label: f.label, hint: f.hint };

    if (f.type === 'toggle') {
      return (
        <FieldToggle
          key={f.key}
          {...common}
          span
          form={form.params}
          set={setParam}
          k={f.key}
        />
      );
    }

    if (f.type === 'number') {
      return (
        <FieldText
          key={f.key}
          {...common}
          form={form.params}
          set={setParam}
          tried={tried}
          k={f.key}
          req={f.required}
          num
          suffix={f.suffix}
        />
      );
    }

    if (f.type === 'select') {
      return (
        <FieldSelect
          key={f.key}
          {...common}
          form={form.params}
          set={setParam}
          tried={tried}
          k={f.key}
          req={f.required}
          options={(f.options ?? []).map((o) => ({ value: String(o.value), label: o.label }))}
        />
      );
    }

    if (f.type === 'multiselect') {
      const current = Array.isArray(form.params[f.key]) ? form.params[f.key] : [];
      const toggleOne = (value) => {
        const next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
        setParam(f.key, next);
      };
      return (
        <FieldCustom
          key={f.key}
          label={f.label}
          req={f.required}
          span
          invalid={tried && f.required && current.length === 0}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(f.options ?? []).map((o) => {
              const on = current.includes(o.value);
              return (
                <button
                  key={String(o.value)}
                  type="button"
                  onClick={() => toggleOne(o.value)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '9px 13px', borderRadius: 9, cursor: 'pointer',
                    fontSize: 13, fontWeight: on ? 600 : 500,
                    border: `1px solid ${on ? 'var(--brand-blue-1, #3b6fd4)' : 'var(--border-color, rgba(15,23,42,0.12))'}`,
                    background: on ? 'rgba(59, 111, 212, 0.10)' : 'var(--surface-2, #f2f6f9)',
                    color: on ? 'var(--brand-blue-1, #3b6fd4)' : 'var(--text-primary)',
                  }}
                >
                  <span aria-hidden>{on ? '✓' : '○'}</span> {o.label}
                </button>
              );
            })}
          </div>
          {f.hint && <div className="dps-ff-hint">{f.hint}</div>}
        </FieldCustom>
      );
    }

    return (
      <FieldText key={f.key} {...common} form={form.params} set={setParam} tried={tried} k={f.key} req={f.required} />
    );
  };

  // ── Zamanlama kartının sıklığa bağlı alanı ─────────────────────────────────────────────────
  const frequencyExtra = () => {
    if (form.frequency === FREQUENCY.HOURLY) {
      return <FieldText form={form} set={set} k="intervalN" label="Her kaç saatte bir" num suffix="saat" />;
    }
    if (form.frequency === FREQUENCY.MONTHLY) {
      return <FieldText form={form} set={set} k="monthDay" label="Ayın günü" num suffix=". gün" hint="Ay o kadar gün çekmiyorsa ayın son gününde çalışır." />;
    }
    return null;
  };

  const toggleWeekday = (no) => {
    const next = form.weekdays.includes(no)
      ? form.weekdays.filter((d) => d !== no)
      : [...form.weekdays, no];
    set('weekdays', next.sort((a, b) => a - b));
  };

  // ── Aksiyonlar ─────────────────────────────────────────────────────────────────────────────
  const actions = (
    <>
      {!isNew && canDelete && (
        <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}>
          <Icon name="x" size={16} /> Sil
        </button>
      )}
      <button className="dps-btn ghost" onClick={() => navigate('/scheduled-tasks')} disabled={saving || running}>İptal</button>
      {canSave && (
        <SplitButton
          label={running ? 'Çalışıyor…' : (saving ? 'Kaydediliyor…' : 'Kaydet')}
          icon="check"
          onClick={() => persist()}
          // İş kuralı 1: görev tipi seçilmeden kaydet AKTİFLEŞMEZ. Tip, hem kriterlerin şemasını
          // hem de görevin ne yapacağını belirler — tipsiz bir görev anlamsızdır.
          disabled={saving || running || !form.taskType}
          menu={isNew
            ? [{ label: 'Kaydet ve Yeni', desc: 'Kaydet, listeye dönme, yeni görev aç', icon: 'plus', onClick: () => persist({ andNew: true }) }]
            : [{ label: 'Şimdi Çalıştır', desc: 'Görevi hemen koştur (zamanlamayı değiştirmez)', icon: 'check', onClick: handleRunNow }]}
        />
      )}
    </>
  );

  const tabs = isNew
    ? null
    : [
      { key: 'definition', label: 'Tanım', icon: 'briefcase' },
      { key: 'runs', label: 'Çalışma Geçmişi', icon: 'clock' },
    ];

  return (
    <DetailShell
      crumb={[
        { label: 'Zamanlanmış Görevler', to: '/scheduled-tasks' },
        { label: isNew ? 'Yeni Görev' : (form.name || 'Görev Detayı'), current: true },
      ]}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'ScheduledTask', entityId: id }}
      loading={loading}
    >
      {activeTab === 'runs' ? (
        <div className="dps-form">
          <Section icon="clock" title="Çalışma Geçmişi" subtitle="Görevin son çalışmaları (en yeni önce)">
            {runs.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', margin: '8px 0 0' }}>
                Bu görev henüz hiç çalışmadı.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '10px 12px' }}>Başlangıç</th>
                      <th style={{ padding: '10px 12px' }}>Süre</th>
                      <th style={{ padding: '10px 12px' }}>Sonuç</th>
                      <th style={{ padding: '10px 12px' }}>Etkilenen</th>
                      <th style={{ padding: '10px 12px' }}>Tetikleyen</th>
                      <th style={{ padding: '10px 12px' }}>Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => {
                      const meta = RUN_STATUS_META[r.status] ?? { label: r.status, tone: 'neutral' };
                      const durationMs = r.finishedAt && r.startedAt
                        ? new Date(r.finishedAt) - new Date(r.startedAt)
                        : null;
                      return (
                        <tr key={r.id} style={{ borderTop: '1px solid var(--border-color, rgba(15,23,42,0.08))' }}>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{formatDateTime(r.startedAt)}</td>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                            {durationMs == null ? '—' : `${(durationMs / 1000).toFixed(1)} sn`}
                          </td>
                          <td style={{ padding: '10px 12px' }}><StatusBadge tone={meta.tone}>{meta.label}</StatusBadge></td>
                          <td style={{ padding: '10px 12px' }}>{r.affectedCount ?? 0}</td>
                          <td style={{ padding: '10px 12px' }}>{TRIGGER_LABEL[r.triggeredBy] || r.triggeredBy || '—'}</td>
                          <td style={{ padding: '10px 12px', color: r.status === 'FAILED' ? '#E53E3E' : 'inherit' }}>
                            {r.message || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      ) : (
        <div className="dps-form">
          {/* 1 — GÖREV TANIMI */}
          <Section icon="briefcase" title="Görev Tanımı" subtitle="Görev tipini seçin; açıklama ve kriterler otomatik gelir">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="name" label="Görev Adı" req placeholder="Örn. Pazar Potansiyel Taşıma" />
              <FieldSelect
                form={form} set={set} tried={tried} k="taskType" label="Görev Tipi" req
                options={[{ value: '', label: 'Seçiniz…' }, ...taskTypes.map((t) => ({ value: t.key, label: t.name }))]}
                onChange={applyTypeDefaults}
              />

              {/* Görev açıklaması SALT OKUNUR — tipten gelir, kullanıcı değiştiremez. */}
              <FieldCustom label="Görev Açıklaması" span>
                <div style={{
                  display: 'flex', gap: 11, padding: '13px 15px', borderRadius: 12,
                  background: 'rgba(59, 111, 212, 0.07)',
                  border: '1px solid rgba(59, 111, 212, 0.22)',
                  color: 'var(--text-primary)', fontSize: 12.5, lineHeight: 1.6,
                }}>
                  <Icon name="info" size={18} />
                  <div>
                    <span style={{
                      fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5,
                      fontWeight: 700, opacity: 0.65, display: 'block', marginBottom: 3,
                    }}>
                      Otomatik · salt okunur
                    </span>
                    {selectedType?.description ?? 'Görev tipi seçildiğinde açıklaması burada görünür.'}
                  </div>
                </div>
              </FieldCustom>

              <FieldTextarea
                form={form} set={set} k="shortNote" span
                label="Kısa Açıklama (kendi notunuz, opsiyonel)"
                placeholder="Bu görevi neden oluşturduğunuza dair kısa not…"
              />
            </Grid>
          </Section>

          {/* 2 — KRİTERLER (tipin şemasından üretilir) */}
          <Section
            icon="layers"
            title="Kriterler / Parametreler"
            subtitle={selectedType?.name ?? 'Seçilen göreve özel'}
          >
            {!selectedType ? (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 20, margin: 0 }}>
                Önce bir görev tipi seçin.
              </p>
            ) : selectedType.paramSchema?.length ? (
              <Grid cols={2}>
                {selectedType.paramSchema.map(renderParamField)}
              </Grid>
            ) : (
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Bu görev tipinin parametresi yok.</p>
            )}
          </Section>

          {/* 3 — ZAMANLAMA (tüm tipler için ORTAK) */}
          <Section icon="clock" title="Zamanlama" subtitle="SQL job mantığında çalışma takvimi">
            <Grid cols={2}>
              <FieldSelect form={form} set={set} k="frequency" label="Sıklık" options={FREQUENCY_OPTIONS} />
              {frequencyExtra()}

              <FieldDate form={form} set={set} tried={tried} k="startDate" label="Başlangıç Tarihi" req />
              <FieldDate form={form} set={set} k="endDate" label="Bitiş Tarihi (boşsa süresiz)" />

              {form.frequency === FREQUENCY.WEEKLY && (
                <FieldCustom
                  label="Çalışacağı gün(ler)"
                  req
                  span
                  invalid={tried && form.weekdays.length === 0}
                >
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {WEEKDAYS.map((d) => {
                      const on = form.weekdays.includes(d.no);
                      return (
                        <button
                          key={d.no}
                          type="button"
                          onClick={() => toggleWeekday(d.no)}
                          title={d.label}
                          style={{
                            width: 46, height: 40, borderRadius: 10, cursor: 'pointer',
                            fontSize: 12.5, fontWeight: 600,
                            border: `1px solid ${on ? 'transparent' : 'var(--border-color, rgba(15,23,42,0.12))'}`,
                            background: on ? 'var(--brand-blue-1, #16233f)' : 'var(--surface-2, #f2f6f9)',
                            color: on ? '#fff' : 'var(--text-primary)',
                          }}
                        >
                          {d.short}
                        </button>
                      );
                    })}
                  </div>
                </FieldCustom>
              )}

              {/* Saat için özel kontrol: DetailShell'de bir FieldTime bileşeni yok. */}
              <FieldCustom label="Çalışma saati">
                <div className="dps-ff-input">
                  <input
                    type="time"
                    value={form.runTime ?? ''}
                    onChange={(e) => set('runTime', e.target.value)}
                  />
                </div>
              </FieldCustom>

              <FieldToggle
                form={form} set={set} k="isActive"
                label="Aktif"
                hint="Pasif görev hiç çalışmaz."
              />

              <FieldCustom label="Sonraki çalışma" span>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  padding: '11px 14px', borderRadius: 10, fontSize: 12.5, fontWeight: 600,
                  background: form.isActive ? 'rgba(63, 157, 85, 0.10)' : 'var(--surface-2, #f2f6f9)',
                  border: `1px solid ${form.isActive ? 'rgba(63, 157, 85, 0.30)' : 'var(--border-color, rgba(15,23,42,0.10))'}`,
                  color: form.isActive ? '#2f7d43' : 'var(--text-secondary)',
                }}>
                  <Icon name="clock" size={16} />
                  {form.isActive
                    ? `${describeSchedule(form)}${!isNew && nextRunAt ? ` · ilk çalışma: ${formatDateTime(nextRunAt)}` : ''}`
                    : 'Görev pasif — çalışmayacak.'}
                </div>
              </FieldCustom>
            </Grid>
          </Section>
        </div>
      )}

      {deleteConfirm && (
        <SubModal
          title="Görevi Sil"
          maxWidth={460}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> görevi silinecek ve bir daha çalışmayacak.
            <br /><br />
            Görevin daha önce yaptığı değişiklikler (ör. Potansiyel yapılan müşteriler)
            <strong> geri alınmaz</strong>.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default ScheduledTaskDetailPage;
